import { Card, Input, Select } from 'antd';
import LOCALS from 'commons/locals';
import { MapPin } from 'lucide-react';
import { CreateRecyclingConsignmentOrderV2SchemaType } from '../common';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import i18n from 'i18n';
import { FormLabel } from '../form-label';
import { useSelector } from 'react-redux';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useCityList } from 'apis/home';
import { SelectOption } from 'types/base';

const LogisticsInput = ({
  control,
  setValue,
}: {
  control: Control<CreateRecyclingConsignmentOrderV2SchemaType>;
  setValue: UseFormSetValue<CreateRecyclingConsignmentOrderV2SchemaType>;
}) => {
  const { countryOptions } = useSelector(selectGlobalInfo);
  const country = useWatch({ control, name: 'country' });
  const { countryCodeOptions } = useSelector(selectGlobalInfo);
  const { cityList } = useCityList(country);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-base font-medium">
          <MapPin className="w-5 h-5" />
          {i18n.t(LOCALS.logistics_information)}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2">
            <div className="flex items-center gap-2">
              <FormLabel required>{i18n.t(LOCALS.name)}:</FormLabel>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <Input
                      placeholder={i18n.t(LOCALS.please_enter) || ''}
                      status={error ? 'error' : undefined}
                      {...field}
                    />
                  );
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <FormLabel>{i18n.t(LOCALS.postal_code)}:</FormLabel>
              <Controller
                name="postCode"
                control={control}
                render={({ field }) => {
                  return (
                    <Input
                      placeholder={i18n.t(LOCALS.please_enter) || ''}
                      {...field}
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="flex items-center gap-2">
              <FormLabel required>{i18n.t(LOCALS.phone_number)}:</FormLabel>

              <Controller
                name="postCountryCode"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <Select
                      placeholder={i18n.t(LOCALS.please_select) || ''}
                      status={error ? 'error' : undefined}
                      style={{
                        minWidth: 128,
                      }}
                      showSearch
                      filterOption={(
                        input: string,
                        option: SelectOption | undefined
                      ) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      {...field}
                      options={countryCodeOptions}
                    />
                  );
                }}
              />
              <Controller
                name="postPhone"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <Input
                      placeholder={i18n.t(LOCALS.please_enter) || ''}
                      status={error ? 'error' : undefined}
                      {...field}
                    />
                  );
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <FormLabel>{i18n.t(LOCALS.phone_number) + '2'}:</FormLabel>
              <Controller
                name="postCountryCode2"
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      style={{
                        minWidth: 128,
                      }}
                      placeholder={i18n.t(LOCALS.please_select) || ''}
                      showSearch
                      filterOption={(
                        input: string,
                        option: SelectOption | undefined
                      ) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      {...field}
                      options={countryCodeOptions}
                    />
                  );
                }}
              />
              <Controller
                name="postPhone2"
                control={control}
                render={({ field }) => {
                  return (
                    <Input
                      placeholder={i18n.t(LOCALS.please_enter) || ''}
                      {...field}
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="flex items-center gap-2">
              <FormLabel required>{i18n.t(LOCALS.country_region)}:</FormLabel>
              <Controller
                name="country"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <Select
                      placeholder={i18n.t(LOCALS.please_select) || ''}
                      className="w-full"
                      status={error ? 'error' : undefined}
                      showSearch
                      filterOption={(
                        input: string,
                        option: SelectOption | undefined
                      ) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      {...field}
                      options={countryOptions}
                      onChange={(value) => {
                        field.onChange(value);
                        setValue('city', '');
                      }}
                    />
                  );
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <FormLabel>{i18n.t(LOCALS.state_province_city)}:</FormLabel>
              <Controller
                name="city"
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      {...field}
                      placeholder={i18n.t(LOCALS.please_select) || ''}
                      className="w-full"
                      showSearch
                      filterOption={(
                        input: string,
                        option: SelectOption | undefined
                      ) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={cityList.map(({ name, code }) => {
                        return {
                          value: code,
                          label: name,
                        };
                      })}
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className="gap-2 flex items-center">
            <FormLabel className="shrink-0 self-start">
              {i18n.t(LOCALS.default_address)}:
            </FormLabel>
            <Controller
              name="detailAddress"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Input.TextArea
                    placeholder={i18n.t(LOCALS.please_enter) || ''}
                    status={error ? 'error' : undefined}
                    {...field}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                );
              }}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default LogisticsInput;
